import { UseLoading } from "@/common/composition/loading";
import { UseCollection } from "@/common/composition/use-collection";
import {
  CreateReportTemplateInput,
  GenerateReportTemplateSection,
  PdfReport,
  ReportTemplate,
  ReportTemplateSection,
} from "./model";
import * as api from "./api";
import { UseFiniteCollection } from "@/common/composition/use-finite-collection";
import { computed, ref } from "@vue/composition-api";
import { omit, uniq } from "@/fp";
import { ToastType, UseToast } from "@/components/root/toast/toast.state";
import { NotificationsManager } from "@/components/root/navbar/notifications/NotificationManager";
import { UseModal } from "@/components/root/modal";
import { UseTabs } from "@/common/composition/use-tabs";
import { trackReportEvent } from "../report-analytics";

const [tabs, current_tab, set_current_tab] = UseTabs(
  [
    {
      id: "Reports",
      name: "Reports",
      icon: "Pdf",
    },
    {
      id: "Templates",
      name: "Templates",
      icon: "Grid",
    },
  ],
  0
);

const pdf_reports = UseCollection<PdfReport, { cursor: string }>(
  api.load_all_pdf_reports
);

const report_questions = UseFiniteCollection<ReportTemplateSection, void>(
  api.load_all_report_template_sections
);

const report_templates = UseCollection<ReportTemplate, { cursor: string }>(
  api.load_all_report_templates
);

const scheduled_reports = computed(() =>
  report_templates.items.value.filter(
    (r) => r.scheduled && r.scheduled !== "none"
  )
);

const current_report_template = ref<null | ReportTemplate>(null);

const sections_to_parameters = (
  sections: Array<
    | ReportTemplateSection
    | GenerateReportTemplateSection
    | PdfReport["sections"][number]
  >
) =>
  sections.map((section) => ({
    type: section.question,
    value: {
      data_source: section.data_source,
      application: section.application,
      parameters: (
        section as {
          parameters_values?: unknown;
          parameters?: unknown;
        }
      ).parameters_values,
      time: (section as { time?: unknown }).time,
    },
  }));

const save_template = async (input: CreateReportTemplateInput) => {
  const res = await api.create_report_template({
    name: input.name,
    access: input.access,
    // @ts-ignore
    sections: input.sections.map(omit("runtime")),
    scheduled: input.scheduled,
    mailing_list: input.mailing_list,
  });
  if (res.success) {
    report_templates.mutate((list) => [res.value, ...list]);
    if (input.scheduled && input.scheduled !== "none") {
      trackReportEvent("scheduled", {
        report_id: res.value.id,
        report_name: input.name,
        report_type: "scheduled",
        report_subtype: "pdf",
        parameters: sections_to_parameters(input.sections),
        data_sources: uniq(input.sections.map((s) => s.data_source)),
        schedule: {
          cadence: input.scheduled,
          recipient_count: input.mailing_list.length,
        },
        source: "pdf_template_editor",
      });
    }
    UseToast().show({
      type: ToastType.SUCCESS,
      msg: "Report Template created successfully",
      duration: 3000,
    });
  } else {
    let msg;
    switch (res.code) {
      case 409:
        msg = "Template with same sections already exists";
        break;
      default:
        msg = "Failed to create report template";
        break;
    }
    UseToast().show({
      type: ToastType.ALERT,
      msg,
      duration: 6000,
    });
  }
};

const create_template_copy = async (template: ReportTemplate) => {
  const res = await UseModal().showSimple({
    title: "Duplicate Template",
    desc: `You are going to create your own copy of 
    <b>${template.name}</b>.
    `,
    submitBtnText: "Duplicate",
    cancelBtnText: "Cancel",
  });
  if (res.type == "success") {
    const input: CreateReportTemplateInput = {
      access: "private",
      mailing_list: [],
      name: "My " + template.name,
      scheduled: "none",
      sections: template.sections,
    };
    save_template(input);
  }
};

const update_template = async (report: ReportTemplate) => {
  const current_report = report_templates.items.value.find(
    (item) => item.id === report.id
  );
  const res = await api.update_report_template({
    ...report,
    // @ts-ignore
    sections: report.sections.map(omit("runtime")),
    scheduled: report.scheduled,
    mailing_list: report.mailing_list,
  });
  if (res.success) {
    report_templates.mutate((list) =>
      list.map((d) =>
        d.id === report.id
          ? { ...report, updated_at: new Date().toISOString() }
          : d
      )
    );
    UseToast().show({
      type: ToastType.SUCCESS,
      msg: "report updated successfully",
      duration: 3000,
    });
    const old_schedule = current_report?.scheduled || "none";
    const new_schedule = report.scheduled || "none";
    const schedule_changed = old_schedule !== new_schedule;
    const mailing_list_changed =
      (current_report?.mailing_list || []).sort().join(",") !==
      (report.mailing_list || []).sort().join(",");
    if (schedule_changed || mailing_list_changed) {
      trackReportEvent(
        old_schedule === "none" && new_schedule !== "none"
          ? "scheduled"
          : new_schedule === "none"
          ? "schedule_deleted"
          : "schedule_updated",
        {
          report_id: report.id,
          report_name: report.name,
          report_type: "scheduled",
          report_subtype: "pdf",
          parameters: sections_to_parameters(report.sections),
          data_sources: uniq(report.sections.map((s) => s.data_source)),
          schedule: {
            cadence: new_schedule,
            recipient_count: report.mailing_list.length,
          },
          source: "pdf_template_schedule",
        }
      );
    }
  } else {
    let msg;
    switch (res.code) {
      case 409:
        msg = "Template with same sections already exists";
        break;
      default:
        msg = "Something went wrong :(";
        break;
    }
    UseToast().show({
      type: ToastType.ALERT,
      msg,
      duration: 3000,
    });
  }
};

const generate_report_from_template = async (payload: {
  name: string;
  sections: GenerateReportTemplateSection[];
  template_id: string;
  tracking_action?: "generated" | "retried";
  /**
   * Optional CTA on the confirmation toast. Generated reports land in the
   * Reports tab, which is obvious when you started there and invisible when
   * you did not — an Executive Report card fires this and the toast then says
   * where the report went and offers to take you.
   */
  toast_action?: { name: string; onClick: () => void };
}) => {
  /* s*/
  const res = await api.create_report(payload);
  if (res.success) {
    trackReportEvent(payload.tracking_action || "generated", {
      report_id: res.value.report.id,
      report_name: payload.name,
      report_type: "pdf",
      report_subtype: "template",
      template_id: payload.template_id,
      status: res.value.report.status,
      parameters: sections_to_parameters(payload.sections),
      data_sources: uniq(payload.sections.map((s) => s.data_source)),
      source: "pdf_report_generator",
    });
    if (res.value.operation == "created") {
      const report = {
        ...res.value.report,
        runtime: {
          data_sources: uniq(
            res.value.report.sections.map((s) => s.data_source)
          ),
        },
      };
      pdf_reports.mutate((list) => [report, ...list]);
      UseToast().show({
        type: ToastType.SUCCESS,
        msg: payload.toast_action
          ? "Your report is being generated in Reports"
          : "your report is in the making",
        duration: payload.toast_action ? 8000 : 3000,
        action: payload.toast_action,
      });
    } else if (
      res.value.operation == "in-progress" ||
      res.value.operation == "pending"
    ) {
      UseToast().show({
        type: ToastType.SUCCESS,
        msg: "your report is already in queue",
        duration: 3000,
      });
    } else if (res.value.operation == "completed") {
      UseToast().show({
        type: ToastType.SUCCESS,
        msg: "The report you requested has already been generated.",
        duration: 10_000,
        action: {
          name: "Download",
          onClick: async () => {
            await api.download_report(res.value.report.id, payload.name);
            trackReportEvent("downloaded", {
              report_id: res.value.report.id,
              report_name: payload.name,
              report_type: "pdf",
              report_subtype: "template",
              template_id: payload.template_id,
              status: res.value.report.status,
              parameters: sections_to_parameters(payload.sections),
              data_sources: uniq(payload.sections.map((s) => s.data_source)),
              source: "pdf_report_already_generated_toast",
            });
          },
        },
      });
    }
  } else {
    UseToast().show({
      type: ToastType.ALERT,
      msg: "Something went wrong :(",
      duration: 3000,
    });
  }
};
const delete_template = async (template_id: string) => {
  const template = report_templates.items.value.find((d) => d.id === template_id);
  const res = await api.delete_report_template(template_id);
  if (res.success) {
    report_templates.mutate((list) => list.filter((d) => d.id !== template_id));
    if (template?.scheduled && template.scheduled !== "none") {
      trackReportEvent("schedule_deleted", {
        report_id: template.id,
        report_name: template.name,
        report_type: "scheduled",
        report_subtype: "pdf",
        parameters: sections_to_parameters(template.sections),
        data_sources: uniq(template.sections.map((s) => s.data_source)),
        schedule: {
          cadence: template.scheduled,
          recipient_count: template.mailing_list.length,
        },
        source: "pdf_template_delete",
      });
    }
    UseToast().show({
      type: ToastType.SUCCESS,
      msg: "Template deleted successfully",
      duration: 3000,
    });
  } else {
    UseToast().show({
      type: ToastType.ALERT,
      msg: "Something went wrong :(",
      duration: 3000,
    });
  }
};

const delete_report = async (report: PdfReport) => {
  console.log("delete_report:", report);
  // this is a long name for a report
  const report_name =
    report.name.length > 20 ? report.name.slice(0, 35) + "..." : report.name;
  const should_delete_res = await UseModal().showSimple({
    title: "Delete Report",
    desc: `Are you sure you want to delete ${report_name} report?`,
    submitBtnText: "Delete",
    cancelBtnText: "Cancel",
  });
  if (should_delete_res.type == "success") {
    const res = await api.delete_report(report.id);
    if (res.success) {
      NotificationsManager.remove(report.id);
      pdf_reports.mutate((list) =>
        list.filter((item) => item.id !== report.id)
      );
      trackReportEvent("deleted", {
        report_id: report.id,
        report_name: report.name,
        report_type: "pdf",
        report_subtype: report.is_scheduled ? "scheduled" : "template",
        template_id: report.template_id,
        status: report.status,
        parameters: sections_to_parameters(report.sections),
        data_sources: report.runtime.data_sources,
        source: "pdf_reports_table",
      });
      // notify notification manage
    } else {
      UseToast().show({
        type: ToastType.ALERT,
        msg: "Something went wrong",
        duration: 3000,
      });
    }
  }
};

const start_checking_reports_status = () => {
  setInterval(async () => {
    const items_which_are_not_finishes = pdf_reports.items.value
      .filter((item) => item.status !== "completed" && item.status !== "failed")
      // @ts-ignore
      .map((d) => (d.id ? d.id : d._id));

    if (items_which_are_not_finishes.length > 0) {
      const res = await api.get_report_status(items_which_are_not_finishes);
      if (res.success) {
        const ready_items = res.value.filter(
          (d) =>
            d.status == "completed" &&
            pdf_reports.items.value.find((a) => a.id === d.id)!.status !==
              "completed"
        );

        pdf_reports.update_items_by(
          "id",
          res.value.map((r) => {
            r.runtime = {
              data_sources: uniq(r.sections.map((s) => s.data_source)),
            };
            return r;
          })
        );
        if (ready_items.length > 0) {
          ready_items.forEach((report) => {
            NotificationsManager.addSimpleAction({
              name: `${report.name} report is ready`,
              icon: "Pdf",
              id: ready_items[0].id,
              type: "simple-action",
              group: "pdf-reports",
              actions: [
                {
                  icon: "Download",
                  name: "Download",
                  action: async (notification) => {
                    await api.download_report(report.id, report.name);
                    trackReportEvent("downloaded", {
                      report_id: report.id,
                      report_name: report.name,
                      report_type: "pdf",
                      report_subtype: report.is_scheduled
                        ? "scheduled"
                        : "template",
                      template_id: report.template_id,
                      status: report.status,
                      parameters: sections_to_parameters(report.sections),
                      data_sources: uniq(
                        report.sections.map((s) => s.data_source)
                      ),
                      source: "pdf_report_ready_notification",
                    });
                    NotificationsManager.remove(notification.id);
                  },
                },
              ],
            });
          });
        }
      }
    }
  }, 10_000);
};

const [is_loading, set_is_loading] = UseLoading(false).as_arr();
const init = async () => {
  set_is_loading(true);
  await Promise.all([
    pdf_reports.load_items({ cursor: "" }),
    report_templates.load_items({ cursor: "" }),
    report_questions.load_items(),
  ]);
  start_checking_reports_status();
  set_is_loading(false);
};

export const pdf_state = {
  init,
  is_loading,
  tabs,
  current_tab,
  set_current_tab,
  pdf_reports,
  template_questions: report_questions.items,
  report_templates,
  current_report_template,
  save_template,
  scheduled_reports,
  generate_report_from_template,
  delete_template,
  update_template,
  create_template_copy,
  delete_report,
};
